// src/pages/SuperAdmin.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Building2,
  BarChart3,
  Scroll,
  Activity,
  Clock,
  Loader2,
} from 'lucide-react';
import SuperAdminStepUpModal from '../components/SuperAdminStepUpModal';
import SuperAdminOrgs from './SuperAdminOrgs';
import SuperAdminOrgDetail from './SuperAdminOrgDetail';
import SuperAdminPlaceholder from './SuperAdminPlaceholder';
import { apiCall } from '../utils/api';

const NAV = [
  { to: 'organizations', icon: Building2,  label: 'Organizations' },
  { to: 'usage',         icon: BarChart3,  label: 'Usage',         placeholder: true },
  { to: 'audit-log',     icon: Scroll,     label: 'Audit Log',     placeholder: true },
  { to: 'system-health', icon: Activity,   label: 'System Health', placeholder: true },
];

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessionActive, setSessionActive] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);  // Date object
  const [remainingMs, setRemainingMs] = useState(0);
  const [showStepUp, setShowStepUp] = useState(false);

  // --- Role check (client-side guard; backend also enforces with 404) ---

  useEffect(() => {
    let roles = [];
    try {
      roles = JSON.parse(localStorage.getItem('user_roles') || '[]');
    } catch (e) {
      // malformed; treat as no roles
    }
    if (!roles.includes('super_admin')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // --- Session status check on mount ---

  const checkSession = useCallback(async () => {
    try {
      const res = await apiCall('/super-admin/session');
      if (res.ok) {
        const body = await res.json();
        if (body.active && body.expires_at) {
          setSessionActive(true);
          setExpiresAt(new Date(body.expires_at));
          setShowStepUp(false);
        } else {
          setSessionActive(false);
          setExpiresAt(null);
          setShowStepUp(true);
        }
      } else if (res.status === 404) {
        // Somehow we got here without super_admin role — redirect
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Session check failed:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkSession(); }, [checkSession]);

  // --- Countdown timer ---

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const ms = expiresAt.getTime() - Date.now();
      setRemainingMs(Math.max(0, ms));
      // Auto-expire: when the session runs out, force step-up again
      if (ms <= 0 && sessionActive) {
        setSessionActive(false);
        setShowStepUp(true);
      }
    };
    update();
    const handle = setInterval(update, 1000);
    return () => clearInterval(handle);
  }, [expiresAt, sessionActive]);

  const handleStepUpSuccess = (expiresAtStr, _sessionMinutes) => {
    setSessionActive(true);
    setExpiresAt(new Date(expiresAtStr));
    setShowStepUp(false);
  };

  const formatCountdown = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  return (
    <div className="flex gap-8">
      {/* Left-rail sub-navigation */}
      <aside className="w-60 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-5 w-5 text-amber-400" />
          <h1 className="text-2xl font-semibold tracking-tight text-white">
            Super Admin
          </h1>
        </div>
        <p className="text-xs text-[#a0a0a0] mb-4">
          Connex-internal tools. Every action is audited.
        </p>

        {/* Session indicator */}
        {sessionActive && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-2 text-xs">
              <Clock className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-amber-300">Session:</span>
              <span className={`font-mono ${remainingMs < 60_000 ? 'text-red-400' : 'text-amber-200'}`}>
                {formatCountdown(remainingMs)}
              </span>
            </div>
          </div>
        )}

        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, icon: Icon, label, placeholder }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-[#1a1a1a] text-white'
                    : 'text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a]/50'
                }`
              }
            >
              <span className="flex items-center gap-3">
                <Icon className="h-4 w-4" />
                {label}
              </span>
              {placeholder && (
                <span className="text-[10px] uppercase tracking-wider text-[#a0a0a0]">
                  soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content area */}
      <div className="flex-1 min-w-0">
        {sessionActive ? (
          <Routes>
            <Route index element={<Navigate to="organizations" replace />} />
            <Route path="organizations" element={<SuperAdminOrgs />} />
            <Route path="organizations/:orgId" element={<SuperAdminOrgDetail />} />
            <Route path="usage" element={
              <SuperAdminPlaceholder
                title="Usage Analytics"
                description="Cross-tenant usage charts, active-customer trends, and capacity planning. Coming after we've shipped more of the product."
              />
            } />
            <Route path="audit-log" element={
              <SuperAdminPlaceholder
                title="Audit Log Viewer"
                description="Browse semantic super-admin actions. The data is already being captured on every action — this page will render it."
              />
            } />
            <Route path="system-health" element={
              <SuperAdminPlaceholder
                title="System Health"
                description="Connector health across all tenants, API error rates, sync job status. Until this is built, use the existing /admin page."
              />
            } />
          </Routes>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
              <ShieldCheck className="h-6 w-6 text-amber-400" />
            </div>
            <p className="text-[#a0a0a0] max-w-sm">
              Verify your password to access super-admin tools.
            </p>
          </div>
        )}
      </div>

      {/* Step-up modal */}
      {showStepUp && (
        <SuperAdminStepUpModal
          onSuccess={handleStepUpSuccess}
          onCancel={() => navigate('/', { replace: true })}
        />
      )}
    </div>
  );
}